import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { SelectionSession } from "../../types";
import { fetchSelectionHistory } from "../../utils/api";
import { AppHeader, Icon, Field } from "../common/ui";
import ResultScreen from "../selection/ResultScreen";

interface Props {
  token: string;
  onExit: () => void;
  theme: "dark" | "light";
  onThemeChange: (t: "dark" | "light") => void;
  /** Open the lesson that teaches the saved scheme (same link as a fresh result). */
  onOpenLesson?: (lessonId: string) => void;
  /** Locked scheme lesson → the plans page. */
  onOpenPlans?: () => void;
}

export default function HistoryView({ token, onExit, theme, onThemeChange, onOpenLesson, onOpenPlans }: Props) {
  const { t, i18n } = useTranslation();
  const isRu = !i18n.language?.startsWith("en");

  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sessions, setSessions] = useState<SelectionSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<SelectionSession | null>(null);

  // Debounced fetch on filter change
  useEffect(() => {
    const id = setTimeout(() => {
      setLoading(true);
      setError(null);
      fetchSelectionHistory(token, { q, date_from: dateFrom || undefined, date_to: dateTo || undefined })
        .then((rows) => setSessions(rows))
        .catch((e) => setError(e instanceof Error ? e.message : t("common.error")))
        .finally(() => setLoading(false));
    }, 300);
    return () => clearTimeout(id);
  }, [q, dateFrom, dateTo, token, t]);

  if (selected) {
    return (
      <ResultScreen
        result={selected.result}
        clientName={selected.client_name}
        calcNumber={selected.number}
        createdAt={selected.created_at}
        onExit={() => setSelected(null)}
        onHome={onExit}
        onOpenLesson={onOpenLesson}
        onOpenPlans={onOpenPlans}
        theme={theme}
        onThemeChange={onThemeChange}
      />
    );
  }

  const fmtDate = (iso: string) =>
    new Date(iso).toLocaleDateString(isRu ? "ru-RU" : "en-GB", {
      day: "2-digit", month: "short", year: "numeric",
    });

  const hasFilters = !!(q || dateFrom || dateTo);

  return (
    <div className="beeapp__scroll">
      <AppHeader theme={theme} onThemeChange={onThemeChange} onBack={onExit} title={t("history.title")} />

      <div className="wrap content-cap" style={{ paddingTop: 6 }}>
        {/* Filters */}
        <div className="glass glass-lg" style={{ padding: 16, marginBottom: 16 }}>
          <Field
            icon="search"
            value={q}
            onChange={setQ}
            placeholder={t("history.searchName")}
          />
          <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}>
            <label className="t-xs muted" style={{ flex: "1 1 130px", minWidth: 0 }}>
              <span style={{ display: "block", marginBottom: 4 }}>{t("history.dateFrom")}</span>
              <input
                type="date" className="input input--bare" value={dateFrom}
                max={dateTo || undefined}
                onChange={(e) => setDateFrom(e.target.value)}
                style={{ width: "100%" }}
              />
            </label>
            <label className="t-xs muted" style={{ flex: "1 1 130px", minWidth: 0 }}>
              <span style={{ display: "block", marginBottom: 4 }}>{t("history.dateTo")}</span>
              <input
                type="date" className="input input--bare" value={dateTo}
                min={dateFrom || undefined}
                onChange={(e) => setDateTo(e.target.value)}
                style={{ width: "100%" }}
              />
            </label>
            {hasFilters && (
              <button
                className="btn btn-glass"
                onClick={() => { setQ(""); setDateFrom(""); setDateTo(""); }}
                style={{ flex: "0 0 auto", alignSelf: "flex-end", paddingInline: 14 }}
              >
                <Icon name="x" size={16} /> {t("history.clear")}
              </button>
            )}
          </div>
        </div>

        {/* List */}
        {loading ? (
          <div style={{ display: "grid", placeItems: "center", padding: 40 }}>
            <div className="spinner spinner-amber" style={{ width: 28, height: 28, borderWidth: 3 }} />
          </div>
        ) : error ? (
          <div className="banner banner-error" role="alert">
            <Icon name="alert" size={18} /> <span>{error}</span>
          </div>
        ) : sessions.length === 0 ? (
          <div className="glass glass-lg" style={{ padding: 32, textAlign: "center" }}>
            <div style={{ display: "grid", placeItems: "center", marginBottom: 12 }}>
              <span style={{
                display: "grid", placeItems: "center", width: 56, height: 56, borderRadius: 16,
                background: "var(--accent-tint)", color: "var(--accent-text)",
              }}>
                <Icon name="history" size={26} />
              </span>
            </div>
            <p className="t-h" style={{ marginBottom: 4 }}>{t("history.emptyTitle")}</p>
            <p className="t-sm muted">{hasFilters ? t("history.emptyFiltered") : t("history.emptyHint")}</p>
          </div>
        ) : (
          <div className="stack stagger" style={{ "--gap": "10px" } as React.CSSProperties}>
            {sessions.map((s) => {
              const swatch = s.result?.color_variants?.[0]?.predicted_color;
              return (
                <button
                  key={s.id}
                  onClick={() => setSelected(s)}
                  className="glass glass-lg glass-sheen"
                  style={{
                    textAlign: "left", cursor: "pointer", padding: 16, color: "inherit",
                    display: "flex", alignItems: "center", gap: 14, border: "none", width: "100%",
                  }}
                >
                  <span style={{
                    width: 44, height: 44, borderRadius: 12, flex: "none",
                    background: swatch || "var(--accent-tint)",
                    boxShadow: swatch ? "inset 0 0 0 1px rgba(0,0,0,0.25), inset 0 2px 5px rgba(255,255,255,0.3)" : "none",
                    display: "grid", placeItems: "center", color: "var(--accent-text)",
                  }}>
                    {!swatch && <Icon name="palette" size={20} />}
                  </span>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div className="t-h" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {s.client_name || t("history.unnamed")}
                    </div>
                    <div className="t-sm muted row" style={{ gap: 8, marginTop: 2 }}>
                      <span className="row" style={{ gap: 4 }}><Icon name="calendar" size={13} /> {fmtDate(s.created_at)}</span>
                      {s.pigment_type && <span style={{ textTransform: "capitalize" }}>· {s.pigment_type}</span>}
                      {s.number != null && <span>· №{s.number}</span>}
                    </div>
                  </div>
                  <Icon name="chevronRight" size={18} />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
